import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Salty Pride: a change that keeps the same photos (e.g. size) leaves the gallery alone.
        if (this.#mediaIds(this) === this.#mediaIds(newMediaGallery)) return;

        this.#keepSelectedSlide(newMediaGallery);
        this.#swapGallery(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Salty Pride: on products with color media (see sp-color-media), a color change keeps the same photo
   * selected: the same standard photo, or the new color's copy at the same position.
   *
   * @param {Element} newMediaGallery - The fetched media gallery that replaces this one.
   */
  #keepSelectedSlide(newMediaGallery) {
    if (!this.hasAttribute('data-sp-color-media') || !newMediaGallery.hasAttribute('data-sp-color-media')) return;

    const { slideshow } = this;
    const newSlideshow = newMediaGallery.querySelector('slideshow-component');
    if (!slideshow || !newSlideshow) return;

    const index = slideshow.current;
    const id = slideshow.slides?.[index]?.getAttribute('slide-id');
    const newSlides = Array.from(newSlideshow.querySelectorAll('slideshow-slide'));
    if (!newSlides.length) return;

    let newIndex = newSlides.findIndex((slide) => slide.getAttribute('slide-id') === id);
    if (newIndex < 0) newIndex = Math.min(index, newSlides.length - 1);

    newSlideshow.setAttribute('initial-slide', String(newIndex));
  }

  /**
   * @param {Element} gallery - A media gallery.
   * @returns {string} The ids of its media, in order.
   */
  #mediaIds(gallery) {
    return Array.from(gallery.querySelectorAll('.product-media[data-media-id]'), (media) =>
      media.getAttribute('data-media-id')
    ).join();
  }

  /**
   * @param {Element} gallery - A media gallery.
   * @returns {Element | undefined} The slide it opens on.
   */
  #initialSlide(gallery) {
    const slideshow = gallery.querySelector('slideshow-component');
    const index = Number(slideshow?.getAttribute('initial-slide')) || 0;
    return slideshow?.querySelectorAll('slideshow-slide')[index];
  }

  /**
   * Salty Pride: loads the photo a fetched page would open on, so it's ready before it's picked.
   *
   * @param {Document} html - The fetched page or section.
   */
  preload(html) {
    const newMediaGallery = html.querySelector('media-gallery');
    if (!newMediaGallery || this.#mediaIds(this) === this.#mediaIds(newMediaGallery)) return;

    this.#keepSelectedSlide(newMediaGallery);
    const source = this.#initialSlide(newMediaGallery)?.querySelector('img');
    if (!source) return;

    const image = new Image();
    image.sizes = source.sizes;
    image.srcset = source.srcset;
    image.src = source.src;
    image.decode().catch(() => {});
  }

  /**
   * Salty Pride: replaces this gallery without showing an empty slide. The current photo moves on top
   * of the new gallery's opening slide and fades out once the new photo can paint.
   *
   * @param {Element} newMediaGallery - The fetched media gallery that replaces this one.
   */
  #swapGallery(newMediaGallery) {
    const newSlide = this.#initialSlide(newMediaGallery);
    const newImage = newSlide?.querySelector('img');

    newMediaGallery.querySelectorAll('img').forEach((image) => (image.decoding = 'sync'));
    if (newImage) {
      newImage.loading = 'eager';
      newImage.fetchPriority = 'high';
    }

    const fade = newSlide ? this.#carryCurrentPhoto(newSlide) : undefined;

    this.replaceWith(newMediaGallery);
    this.#scrollToSlide(newSlide);

    if (!fade || !newImage) return;

    newImage
      .decode()
      .catch(() => {})
      .then(() => {
        if (!fade.isConnected) return;
        const duration = prefersReducedMotion() ? 0 : 200;
        const animation = fade.animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing: 'ease', fill: 'forwards' });
        animation.finished.catch(() => {}).then(() => fade.remove());
      });
  }

  /**
   * @param {Element} newSlide - The slide to carry the photo onto.
   * @returns {HTMLElement | undefined} The element holding the photo.
   */
  #carryCurrentPhoto(newSlide) {
    const { slideshow } = this;
    const slide = slideshow?.slides?.[slideshow.current];
    if (!slide) return undefined;

    // A photo still waiting on the last change stays up, since the last change's photo never showed.
    const waiting = slide.querySelector('.sp-media-fade');
    if (waiting instanceof HTMLElement && !waiting.getAnimations().length) {
      newSlide.append(waiting);
      return waiting;
    }

    const image = slide.querySelector('.product-media__image');
    if (!image) return undefined;

    const fade = document.createElement('div');
    fade.className = 'sp-media-fade product-media';
    image.removeAttribute('ref');
    fade.append(image);
    newSlide.append(fade);
    return fade;
  }

  /**
   * Puts the slideshow on its slide before the next paint, instead of a frame or two after it.
   *
   * @param {Element | undefined} slide - The slide to show.
   */
  #scrollToSlide(slide) {
    const scroller = slide?.parentElement;
    if (!scroller || !slide.previousElementSibling) return;

    const left = scroller.scrollLeft + slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
    scroller.scrollTo({ left, behavior: 'instant' });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
